import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Link, useLocation as useLoc } from "react-router-dom";
import { NAV } from "@/data/content";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export const Header = () => {
  const { pathname } = useLoc();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-sm" : "bg-gradient-to-b from-black/45 via-black/20 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img src="/logo.png" alt="Risi Wellness" width="48" height="48" decoding="async" className={`h-12 w-12 object-contain rounded-full transition-colors ${scrolled ? "" : "bg-white/85 p-0.5"}`} />
          <span className="hidden sm:block leading-none">
            <span className={`block font-serif text-xl font-semibold tracking-wide ${scrolled ? "text-primary" : "text-white"}`}>RISI</span>
            <span className={`block text-[0.6rem] tracking-[0.35em] ${scrolled ? "text-muted-foreground" : "text-white/70"}`}>WELLNESS LTD</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? pathname === n.to ? "text-primary" : "text-foreground/70 hover:text-primary"
                  : pathname === n.to ? "text-white" : "text-white/80 hover:text-white"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/booking"
            data-testid="header-book-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            Book Now
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 transition-colors ${scrolled || open ? "text-primary" : "text-white"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background/95 backdrop-blur-xl border-b border-border overflow-hidden"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className="py-3 text-base font-medium text-foreground/80 border-b border-border/50 last:border-0"
                >
                  {n.label}
                </Link>
              ))}
              <Link
                to="/booking"
                onClick={() => setOpen(false)}
                className="mt-3 text-center rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold"
              >
                Book Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
